import { zodResolver } from '@hookform/resolvers/zod'
import type { AgentNodeData } from '@saaga/shared/interfaces/agency-node.interface'
import { useCallback } from 'react'
import { useForm } from 'react-hook-form'

import {
  type AgentNodeFormSchemaType,
  agentNodeFormSchema,
  defaultAgentNodeFormValues,
} from './agency-node-form.schema'
import { useAgentNodeFormReset } from './use-agent-node-form-reset.hook'
import { useAgentNodeImage } from './use-agent-node-image.hook'
import { useAgentNodeUnsavedChanges } from './use-agent-node-unsaved-changes.hook'

interface UseAgentNodeDrawerProps {
  nodeData: AgentNodeData | null
  nodeId: string | null
  onSave: (nodeId: string, data: Partial<AgentNodeData>) => void
  onOpenChange: (isOpen: boolean) => void
}

export const useAgentNodeDrawer = ({
  nodeData,
  nodeId,
  onSave,
  onOpenChange,
}: UseAgentNodeDrawerProps) => {
  const form = useForm<AgentNodeFormSchemaType>({
    resolver: zodResolver(agentNodeFormSchema),
    defaultValues: defaultAgentNodeFormValues,
  })

  const { imagePreviewUrl, handleImageChange, handleImageRemove } = useAgentNodeImage(
    form,
    nodeData?.imageUrl
  )
  const unsavedChanges = useAgentNodeUnsavedChanges({ form, onOpenChange })

  useAgentNodeFormReset(form, nodeData, nodeId)

  const handleSubmit = useCallback(
    (values: AgentNodeFormSchemaType): void => {
      if (!nodeId) return
      onSave(nodeId, values)
      onOpenChange(false)
    },
    [nodeId, onSave, onOpenChange]
  )

  return {
    form,
    imagePreviewUrl,
    isUnsavedDialogOpen: unsavedChanges.isUnsavedDialogOpen,
    handleImageChange,
    handleImageRemove,
    handleSubmit,
    handleCancel: unsavedChanges.handleCloseAttempt,
    handleCloseAttempt: unsavedChanges.handleCloseAttempt,
    handleForceClose: unsavedChanges.handleForceClose,
    handleUnsavedDialogClose: unsavedChanges.handleUnsavedDialogClose,
  }
}
